import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import { getProStatus } from "../../services/proApi";
import { getProNotifications } from "../../services/proSystemApi";
import "./Pro.css";

// =====================================================
// PRO HOME  (Step 4)
//
// The dashboard a Pro member lands on. It shows EXACTLY the six features
// Step 4 lists, each opening a real page:
//
//   Write Blog · My Pro Profile · My Pro Users · My Earnings & Payout ·
//   Pro Notifications · Pro Settings / Bank
//
// ── WHAT WAS REMOVED (Part B item 1) ──
// This page used to carry a seventh tile, "Analytics", plus a placeholder
// dialog for the three modules that did not exist yet. The Analytics tile is
// gone — not disabled, not hidden — and so is the code behind it. The
// placeholder dialog is gone too, because every remaining tile now opens
// something real.
//
// The only numbers on this page are the membership dates and the unread
// notification count. There is no performance widget of any kind.
// =====================================================

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function ProHome() {
  const navigate = useNavigate();

  const [membership, setMembership] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await getProStatus();
        if (!cancelled && data?.success) setMembership(data);
      } catch (err) {
        // The route guard already proved membership; a failed status read just
        // means the summary strip has nothing to show.
      }

      try {
        const notifications = await getProNotifications();
        if (!cancelled) setUnread(notifications?.unreadCount || 0);
      } catch (err) {
        // A badge is not worth an error state.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // The six Step 4 options, in the order the specification lists them.
  const OPTIONS = [
    {
      key: "write",
      icon: "✍️",
      label: "Write Blog",
      hint: "Publish a Pro blog",
      to: "/create-content",
    },
    {
      key: "profile",
      icon: "💎",
      label: "My Pro Profile",
      hint: "Your Pro blogs and stats",
      to: "/pro-profile",
    },
    {
      key: "users",
      icon: "👥",
      label: "My Pro Users",
      hint: "Readers who paid you",
      to: "/pro-users",
    },
    {
      key: "earnings",
      icon: "💰",
      label: "My Earnings & Payout",
      hint: "Balance, monthly earnings, payouts",
      to: "/pro-earnings",
    },
    {
      key: "notifications",
      icon: "🔔",
      label: "Pro Notifications",
      hint: "Payments, likes, comments, shares, payouts",
      to: "/pro-notifications",
      badge: unread,
    },
    {
      key: "bank",
      icon: "🏦",
      label: "Pro Settings & Bank",
      hint: "Bank details and email verification",
      to: "/pro-bank",
    },
  ];

  return (
    <div className="pro-page-wrap">
      <Navbar />

      <div className="pro-container">
        <header className="pro-hero">
          <div className="pro-hero-badge">💎 PRO MEMBER</div>
          <h1 className="pro-hero-title">TheBlogsSphere Pro</h1>
          <p className="pro-hero-sub">Your Pro Home</p>

          {membership && (
            <div className="pro-membership-strip">
              <span className="pro-chip pro-chip-live">🟢 Active</span>
              <span className="pro-chip">
                Valid till <strong>{formatDate(membership.expiryDate)}</strong>
              </span>
              <span className="pro-chip">
                <strong>{membership.daysRemaining}</strong> days left
              </span>
            </div>
          )}
        </header>

        <nav className="pro-option-grid" aria-label="TheBlogsSphere Pro menu">
          {OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className="pro-option-card"
              onClick={() => navigate(option.to)}
            >
              <span className="pro-option-icon" aria-hidden="true">
                {option.icon}
              </span>
              <strong className="pro-option-label">
                {option.label}
                {option.badge > 0 && (
                  <span className="pro-tab-count" style={{ marginLeft: 8 }}>
                    {option.badge}
                  </span>
                )}
              </strong>
              <small className="pro-option-hint">{option.hint}</small>
            </button>
          ))}
        </nav>

        {/* Not one of the six — this is the READER side of Pro (Step 10), and a
            creator is a reader of other creators too. Kept as a secondary link
            so the six dashboard options stay exactly the six. */}
        <div className="pro-section-head pro-section-head-right">
          <button
            type="button"
            className="pro-btn pro-btn-ghost pro-btn-sm"
            onClick={() => navigate("/pro-access")}
          >
            🔓 My Pro Access
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProHome;
